import { Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useOrgHeartbeatSnapshot } from 'api/aws/heartbeat/useOrgHeartbeatSnapshot'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

/**
 * Heartbeat / NetSuite tab on the organization profile page.
 *
 * Renders a snapshot of the linked Heartbeat account: subscriptions,
 * contracts, recent submissions, and cross-system IDs. Resolves the
 * Heartbeat account via the org's `heartbeat_account_id`. When the org
 * isn't linked, surfaces a specific message so ops knows what to do.
 */

const formatCurrency = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return '—'
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const formatDate = (v: string | null | undefined): string => {
  if (!v) return '—'
  const d = dayjs(v)
  return d.isValid() ? d.format('YYYY-MM-DD') : '—'
}

const parseServiceLines = (v: string[] | string | null): string[] => {
  if (!v) return []
  if (Array.isArray(v)) return v
  try {
    const parsed = JSON.parse(v)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const OrgHeartbeatData = (): JSX.Element => {
  const { PK } = useParams<{ PK: string }>()
  const { data, error, isLoading } = useOrgHeartbeatSnapshot(PK)

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading Heartbeat snapshot…
        </Typography>
      </Box>
    )
  }

  if (error) {
    // Error payload from our backend has a stable `code` we can switch on.
    const errBody = error.response?.data
    const code = errBody?.code

    if (code === 'heartbeat_account_unlinked') {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              No Heartbeat account linked to this organization
            </Typography>
            <Typography variant="body2">
              This organization does not have a <code>heartbeat_account_id</code> set.
              Ops needs to create or link the corresponding Heartbeat account before
              subscription and contract data can be displayed here.
            </Typography>
          </Alert>
        </Box>
      )
    }

    if (code === 'org_not_found') {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Organization not found in MOTHERSHIP for PK {PK}.</Alert>
        </Box>
      )
    }

    if (code === 'heartbeat_unreachable' || code === 'heartbeat_error') {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="subtitle2">Heartbeat is unreachable right now</Typography>
            <Typography variant="body2">{errBody?.error}</Typography>
          </Alert>
        </Box>
      )
    }

    if (code === 'heartbeat_account_not_found') {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Heartbeat account ID is set but the account no longer exists
            </Typography>
            <Typography variant="body2">
              {errBody?.error}. The link may be stale — re-run the NetSuite/Heartbeat
              matcher or update <code>heartbeat_account_id</code> manually.
            </Typography>
          </Alert>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load Heartbeat snapshot: {errBody?.error ?? error.message}
        </Alert>
      </Box>
    )
  }

  if (!data) return <Box />

  const { account, subscriptions, contracts, recent_submissions, counts } = data.snapshot

  return (
    <Box sx={{ p: 2, maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
      {/* Header — account summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h6">{account.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
              {account.type && <Chip label={account.type} size="small" />}
              <Chip
                label={account.active ? 'Active' : 'Inactive'}
                size="small"
                color={account.active ? 'success' : 'default'}
              />
              {account.archived && <Chip label="Archived" size="small" color="warning" />}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Heartbeat Account ID
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {data.heartbeat_account_id}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mt: 2 }} flexWrap="wrap">
          {account.external_ids.netsuite_id && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                NetSuite
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {account.external_ids.netsuite_id}
              </Typography>
            </Box>
          )}
          {account.external_ids.freshsale_id && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Freshsales
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {account.external_ids.freshsale_id}
              </Typography>
            </Box>
          )}
          {account.external_ids.click_up_id && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                ClickUp
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {account.external_ids.click_up_id}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Subscriptions */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1">
            Subscriptions{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({counts.subscriptions_active} active / {counts.subscriptions_total} total)
            </Typography>
          </Typography>
        </Box>
        {subscriptions.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No subscriptions found for this account.
            </Typography>
          </Box>
        ) : (
          (() => {
            const totalRecurring = subscriptions.reduce(
              (sum, s) => sum + (typeof s.recurring_amount === 'number' ? s.recurring_amount : 0),
              0,
            )
            const totalContractValue = subscriptions.reduce(
              (sum, s) => sum + (typeof s.total === 'number' ? s.total : 0),
              0,
            )
            return (
              <TableContainer>
                <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service Line</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Recurring</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptions.map(s => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          {s.service_line?.code || s.subscription_item?.service_line || '—'}
                        </TableCell>
                        <TableCell>{s.subscription_item?.display_name || s.name || '—'}</TableCell>
                        <TableCell>
                          {s.status && (
                            <Typography variant="body2" sx={{ color: renderColorByString(s.status), fontWeight: 500 }}>
                              {s.status}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{s.frequency || '—'}</TableCell>
                        <TableCell>{formatCurrency(s.recurring_amount)}</TableCell>
                        <TableCell>{formatCurrency(s.total)}</TableCell>
                        <TableCell>{formatDate(s.start_date)}</TableCell>
                        <TableCell>{formatDate(s.end_date)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Aggregate row — sums Recurring + Total across all subscriptions */}
                    <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' } }}>
                      <TableCell colSpan={4}>Total</TableCell>
                      <TableCell>{formatCurrency(totalRecurring)}</TableCell>
                      <TableCell>{formatCurrency(totalContractValue)}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )
          })()
        )}
      </Paper>

      {/* Contracts */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1">
            Contracts{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({counts.contracts_total} total)
            </Typography>
          </Typography>
        </Box>
        {contracts.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No contracts found for this account.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Service Lines</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>External ID</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map(c => {
                  const lines = parseServiceLines(c.service_lines)
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{formatDate(c.start_date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {lines.length > 0 ? (
                            lines.map(l => <Chip key={l} label={l} size="small" variant="outlined" />)
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {c.external_source || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {c.external_id || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {c.note || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Recent submissions */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1">
            Recent Submissions{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              (last {counts.submissions_recent})
            </Typography>
          </Typography>
        </Box>
        {recent_submissions.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No submissions found.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Effective</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent_submissions.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>{formatDate(s.created_at)}</TableCell>
                    <TableCell>{s.type || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {s.status && (
                          <Typography variant="body2" sx={{ color: renderColorByString(s.status), fontWeight: 500 }}>
                            {s.status}
                          </Typography>
                        )}
                        {s.altered_status && (
                          <Typography
                            variant="body2"
                            sx={{ color: renderColorByString(s.altered_status), fontWeight: 500 }}
                          >
                            {s.altered_status}
                          </Typography>
                        )}
                        {s.churn_scope && (
                          <Typography
                            variant="body2"
                            sx={{ color: renderColorByString(s.churn_scope), fontWeight: 500 }}
                          >
                            {s.churn_scope}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(s.requested_date)}</TableCell>
                    <TableCell>{formatDate(s.effective_date)}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {s.notes || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}

export default OrgHeartbeatData
